import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

type ConsentStatus = "accepted" | "rejected" | null;

interface CookieConsentContextType {
    analyticsConsent: ConsentStatus;
    acceptAnalytics: () => void;
    rejectAnalytics: () => void;
    isConsentGiven: boolean;
}

const CONSENT_KEY = "targetcalc_cookie_consent";

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [analyticsConsent, setAnalyticsConsent] = useState<ConsentStatus>(() => {
        const stored = localStorage.getItem(CONSENT_KEY);
        if (stored === "accepted" || stored === "rejected") return stored;
        return null;
    });

    const acceptAnalytics = useCallback(() => {
        localStorage.setItem(CONSENT_KEY, "accepted");
        setAnalyticsConsent("accepted");
    }, []);

    const rejectAnalytics = useCallback(() => {
        localStorage.setItem(CONSENT_KEY, "rejected");
        setAnalyticsConsent("rejected");
    }, []);

    const isConsentGiven = analyticsConsent !== null;

    return (
        <CookieConsentContext.Provider value={{ analyticsConsent, acceptAnalytics, rejectAnalytics, isConsentGiven }}>
            {children}
        </CookieConsentContext.Provider>
    );
};

export const useCookieConsent = (): CookieConsentContextType => {
    const context = useContext(CookieConsentContext);
    if (!context) {
        throw new Error("useCookieConsent must be used within a CookieConsentProvider");
    }
    return context;
};
